/**
 * English strings — the SOURCE OF TRUTH for translation keys.
 *
 * `ml.ts` is typed as `Record<keyof typeof en, string>`, so adding a key here
 * without adding it there is a compile error. That is deliberate: the academy
 * owner's standing instruction is that anything a parent comes across must be
 * translated, including work done later, and a promise to remember is worth
 * less than a build failure.
 *
 * Scope: parent-facing surfaces only. Staff chrome, admin settings and the
 * operational modules stay English — owner's decision, recorded in
 * docs/implementation-plan.md (the i18n row of the decisions table).
 *
 * Conventions:
 *   - Keys are dot-namespaced by surface: `nav.*`, `fees.*`, `auth.*`.
 *   - `{placeholder}` marks an interpolated value.
 *   - A plural pair is `key_one` / `key_other`; call it with `{ count }`.
 *   - Do NOT translate the academy's own vocabulary. `hifz`, `tajweed`,
 *     `nazira` and `nalim` are the words these families already use; the
 *     Malayalam file transliterates them into Malayalam script rather than
 *     replacing them with descriptions. Owner's explicit decision.
 */
export const en = {
  // ── Shell chrome ──────────────────────────────────────────────────────────
  'nav.skipToContent': 'Skip to main content',
  'nav.primary': 'Primary',
  'nav.new': 'NEW',
  'nav.openMenu': 'Open menu',
  'nav.closeMenu': 'Close menu',
  'nav.signOut': 'Sign out',

  // ── Parent navigation ─────────────────────────────────────────────────────
  'nav.family': 'Family',
  'nav.myChildren': 'My children',
  'nav.myFees': 'My fees',
  'nav.myAttendance': 'My attendance',
  'nav.announcements': 'Announcements',
  'nav.myMarks': 'My marks',
  'nav.teacherFeedback': 'Teacher feedback',
  'nav.referralsWallet': 'Referrals & wallet',

  // ── Family landing ────────────────────────────────────────────────────────
  'family.title': 'Family',
  'family.contactOffice': 'Please contact the academy office.',
  'family.intro': 'Your children, dues, and recent updates from the academy.',
  'family.kpi.children': 'Children',
  'family.kpi.noChildrenLinked': 'No children linked yet',
  'family.kpi.feeDue': 'Fee due',
  'family.kpi.dueOn': 'Due {date}',
  'family.kpi.nothingOutstanding': 'Nothing outstanding',
  'family.kpi.pendingFeedback': 'Pending feedback',
  'family.kpi.feedbackDisabled': 'Feedback module not enabled yet',
  'family.kpi.caughtUp': 'You are caught up',
  'family.kpi.openBelow': 'Open from the new section below',
  'family.kpi.wallet': 'Wallet',
  'family.kpi.walletDisabled': 'Wallet not enabled yet',
  'family.kpi.acrossAccounts_one': 'Across {count} account',
  'family.kpi.acrossAccounts_other': 'Across {count} accounts',
  'family.noRecentRemarks': 'No recent remarks',
  'family.noChildrenLinkedTitle': 'No children linked',
  'family.remarkFor': 'For',
  'family.lastRemark': 'Last remark:',
  'family.attendanceThisMonth': 'Attendance this month:',
  'family.needHelp': 'Need to update an attendance record or query a fee?',
  'family.openAnnouncements': 'Open announcements',
  'family.forContactDetails': 'for academy contact details.',
  'family.addFirstChild': 'Add your first child',
  'family.recentMarks': 'Recent marks',
  'family.noMarksYet': 'No marks yet',
  'family.marksWillAppear': 'Exam results will appear here once teachers publish them.',
  'family.examsDisabled': 'Exam results and progress will appear here once the Exams module is enabled.',
  'family.referralCodes': 'Referral codes',
  'family.noCodesYet': 'No codes yet',
  'family.codesWillAppear': 'Once your child enrols, your shareable referral code will appear here.',
  'family.referralsDisabled':
    'Share your child\'s unique code and earn wallet credits — coming once Referrals is enabled.',
  'family.childFallback': 'Child',
  'family.childCount_one': '{count} child',
  'family.childCount_other': '{count} children',

  // ── Fees ──────────────────────────────────────────────────────────────────

  // ── Attendance ────────────────────────────────────────────────────────────

  // ── Shared states ─────────────────────────────────────────────────────────

  // ── Auth (a parent's first screen) ────────────────────────────────────────
} as const;

/** Every valid translation key. `ml.ts` must cover all of them. */
export type TranslationKey = keyof typeof en;
